/**
 * De onde a Central de Reclamações tira os dados.
 *
 * Tenta primeiro o backend (`GET /api/bridge/reclamacoes`, servido pelo coletor
 * do fiesta-api). Se o endpoint não responder, ou ainda não tiver caso nenhum
 * gravado, cai no JSON estático da última leitura manual — e diz na tela qual
 * das duas fontes está no ar. O que não pode acontecer é a analista olhar para
 * um dado de setembro achando que é de hoje.
 *
 * O documento que o backend devolve NÃO tem a mesma forma que a tela usa: lá é
 * snake_case, com `prazo` aninhado e `mensagens` como lista; aqui é camelCase e
 * `mensagens` é contagem. A tradução mora neste arquivo, de propósito — o painel
 * não deve conhecer o schema do banco, e o banco não deve se moldar ao painel.
 */

import { fetchReclamacoes, type CasoBridge, type ReclamacoesPayload } from '../../api/bridge'
import type { CasoMl, ContaMl, DadosMl, DadosShopee, FilaShopee, MotivoShopee } from './logic'

export type Fonte = 'bridge' | 'arquivo'

export type DadosReclamacoes = {
  ml: DadosMl
  shopee: DadosShopee
  fonte: Fonte
  /** por que caiu no arquivo, quando caiu */
  motivoFallback?: string
  /** 'api' = consultado agora; 'banco' = retrato gravado */
  origem?: 'api' | 'banco' | 'api+banco'
  /** canal que não respondeu, fila servida de retrato, etc. */
  avisos?: string[]
}

// ---------------------------------------------------------------- adaptação

const ehMl = (c: CasoBridge) => c.canal === 'mercado_livre'

const claimIdDe = (c: CasoBridge) => c.claimId || c.claim_id || ''
const abertaEmDe = (c: CasoBridge) => c.abertaEm || c.aberta_em || ''
const motivoCodDe = (c: CasoBridge) => c.motivoCod || c.motivo_cod || ''
const motivoLabelDe = (c: CasoBridge) => c.motivoLabel || c.motivo_label || 'Outros'
const itemTituloDe = (c: CasoBridge) => c.itemTitulo || c.item_titulo || null
const temPrazoDe = (c: CasoBridge) => Boolean(c.prazo?.temPrazo ?? c.prazo?.tem_prazo)
const noPrazoDe = (c: CasoBridge) => {
  const n = c.prazo?.noPrazo ?? c.prazo?.no_prazo
  return typeof n === 'number' ? n : -1
}

/** `noPrazo` da tela: 1 respondeu dentro, 0 perdeu, -1 não se aplica. */
const adaptarMl = (c: CasoBridge): CasoMl => ({
  id: claimIdDe(c),
  conta: (c.conta === 'H' ? 'H' : 'F') as ContaMl,
  motivoCod: motivoCodDe(c),
  motivoLabel: motivoLabelDe(c),
  hot: Boolean(c.hot),
  data: abertaEmDe(c).slice(0, 10),
  aberta: Boolean(c.aberta),
  temPrazo: temPrazoDe(c),
  noPrazo: noPrazoDe(c),
  // a tela mostra quantas mensagens houve; o texto fica para o rascunho LLM (E.1)
  mensagens: Array.isArray(c.mensagens) ? c.mensagens.length : 0,
  mensagensDetalhe: Array.isArray(c.mensagens)
    ? c.mensagens.map((m) => ({
        de: m?.de,
        em: m?.em ?? null,
        texto: m?.texto,
      }))
    : undefined,
  mlb: c.mlb ?? null,
  itemTitulo: itemTituloDe(c),
  valor: typeof c.valor === 'number' ? c.valor : null,
  solucao: c.solucao
    ? {
        aplicada: Boolean(c.solucao.aplicada),
        label: c.solucao.label ?? null,
        tipo: c.solucao.tipo ?? null,
      }
    : null,
})

const adaptarShopee = (c: CasoBridge): FilaShopee => {
  const aberta = abertaEmDe(c)
  return {
    loja: c.loja === 'DECORA' ? 'DECORA' : 'FESTA',
    lojaCod: c.conta === 'L2' ? 'L2' : 'L1',
    sn: claimIdDe(c),
    status: c.status || '',
    motivoCod: motivoCodDe(c),
    valor: typeof c.valor === 'number' ? c.valor : 0,
    data: aberta.slice(8, 10) + '/' + aberta.slice(5, 7),
    pendente: Boolean(c.pendente),
    produto: itemTituloDe(c) || '—',
    // na Shopee o texto do comprador vem junto do caso
    texto: c.mensagens?.[0]?.texto || '',
  }
}

/**
 * Agregado por motivo, recalculado a partir dos casos.
 *
 * Quando a fonte é o arquivo, esses números vêm prontos da leitura manual (com
 * o quanto foi cancelado sozinho, que só se sabe olhando o histórico). Vindo do
 * backend, são recontados aqui — e só enxergam o que está no recorte pedido.
 */
function motivosDeCasos(casos: FilaShopee[], brutos: CasoBridge[]): MotivoShopee[] {
  const porMotivo = new Map<string, MotivoShopee>()
  casos.forEach((caso, i) => {
    const bruto = brutos[i]
    const chave = caso.motivoCod
    const atual =
      porMotivo.get(chave) ||
      ({
        cod: chave,
        label: bruto ? motivoLabelDe(bruto) : chave,
        festa: { total: 0, canceladas: 0, valor: 0 },
        decora: { total: 0, canceladas: 0, valor: 0 },
      } as MotivoShopee)
    const lado = caso.lojaCod === 'L1' ? atual.festa : atual.decora
    lado.total += 1
    lado.valor += caso.valor
    if ((bruto?.status || '').toUpperCase() === 'CANCELLED') lado.canceladas += 1
    porMotivo.set(chave, atual)
  })
  return [...porMotivo.values()]
}

export function adaptarPayload(payload: ReclamacoesPayload): { ml: DadosMl; shopee: DadosShopee } {
  const casos = payload.casos || []
  const casosMl = casos.filter(ehMl)
  const brutosShopee = casos.filter((c) => !ehMl(c))
  const filaShopee = brutosShopee.map(adaptarShopee)

  const coletadoEm = (payload.meta?.coletado_em || '').slice(0, 10)
  const periodo = {
    de: payload.meta?.periodo?.de || '',
    ate: payload.meta?.periodo?.ate || '',
  }

  return {
    ml: {
      meta: {
        fonte: 'API do Mercado Livre via coletor do fiesta-api',
        coletado_em: coletadoEm,
        periodo,
        contas: { F: 'FIESTAPARTYFESTA (777058595)', H: 'FIESTAPARTYHOME (3131364517)' },
        is_sample: false,
      },
      itens: payload.itens || [],
      casos: casosMl.map(adaptarMl),
    },
    shopee: {
      meta: {
        fonte: 'API da Shopee via coletor do fiesta-api',
        coletado_em: coletadoEm,
        periodo,
        totais_declarados: {
          devolucoes: filaShopee.length,
          valor: filaShopee.reduce((s, c) => s + c.valor, 0),
          festa: filaShopee.filter((c) => c.lojaCod === 'L1').length,
          decora: filaShopee.filter((c) => c.lojaCod === 'L2').length,
        },
      },
      // o gráfico diário saiu da tela; a série só existe no arquivo
      serieDiaria: [],
      motivos: motivosDeCasos(filaShopee, brutosShopee),
      fila: filaShopee.filter((c) => {
        const bruto = brutosShopee.find((b) => b.claim_id === c.sn)
        return bruto?.aberta !== false
      }),
    },
  }
}

// ---------------------------------------------------------------- carregamento

async function doArquivo(): Promise<{ ml: DadosMl; shopee: DadosShopee }> {
  const json = async <T,>(url: string): Promise<T> => {
    const res = await fetch(url)
    if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`)
    return (await res.json()) as T
  }
  const [ml, shopee] = await Promise.all([
    json<DadosMl>('/data/central-reclamacoes-ml.json'),
    json<DadosShopee>('/data/central-reclamacoes-shopee.json'),
  ])
  return { ml, shopee }
}

/**
 * Histórico gravado — a base da aba de análise.
 *
 * Vem do banco de propósito: caso encerrado não muda mais, e varrer 60 dias de
 * API a cada gráfico aberto seria lento sem ganhar nada. Quem precisa de agora
 * é a fila, e essa lê a API.
 */
export async function carregarHistorico(dias = 60): Promise<DadosReclamacoes> {
  const ate = new Date()
  const de = new Date(ate)
  de.setDate(de.getDate() - dias)
  const iso = (d: Date) => d.toISOString().slice(0, 10)

  try {
    const payload = await fetchReclamacoes({ de: iso(de), ate: iso(ate), fonte: 'banco' })
    if (payload?.casos?.length) {
      return {
        ...adaptarPayload(payload),
        fonte: 'bridge',
        origem: 'banco',
        avisos: payload.meta?.avisos || [],
      }
    }
    const arquivo = await doArquivo()
    return {
      ...arquivo,
      fonte: 'arquivo',
      motivoFallback:
        'o histórico no banco está vazio — o job de coleta dos 60 dias ainda não rodou',
    }
  } catch (err) {
    const arquivo = await doArquivo()
    return { ...arquivo, fonte: 'arquivo', motivoFallback: explicarFalha(err) }
  }
}

export async function carregarReclamacoes(
  periodoDias = 60,
  fonteBackend: 'api' | 'banco' = 'api',
): Promise<DadosReclamacoes> {
  const ate = new Date()
  const de = new Date(ate)
  de.setDate(de.getDate() - periodoDias)
  const iso = (d: Date) => d.toISOString().slice(0, 10)

  try {
    const payload = await fetchReclamacoes({ de: iso(de), ate: iso(ate), fonte: fonteBackend })
    if (payload?.casos?.length) {
      return {
        ...adaptarPayload(payload),
        fonte: 'bridge',
        origem: payload.meta?.origem,
        avisos: payload.meta?.avisos || [],
      }
    }
    // fila ao vivo vazia: tenta o retrato do banco antes do JSON estático
    if (fonteBackend === 'api') {
      try {
        const doBanco = await carregarHistorico(periodoDias)
        if (doBanco.fonte === 'bridge') {
          return {
            ...doBanco,
            avisos: [
              ...(doBanco.avisos || []),
              'a API ao vivo não devolveu casos; mostrando o último retrato gravado',
            ],
          }
        }
      } catch {
        /* cai no arquivo abaixo */
      }
    }
    // endpoint no ar mas sem caso gravado: o coletor ainda não rodou
    const arquivo = await doArquivo()
    return {
      ...arquivo,
      fonte: 'arquivo',
      motivoFallback: 'o endpoint respondeu sem nenhum caso — o coletor ainda não rodou',
    }
  } catch (err) {
    if (fonteBackend === 'api') {
      try {
        const doBanco = await carregarHistorico(periodoDias)
        if (doBanco.fonte === 'bridge') {
          return {
            ...doBanco,
            avisos: [
              ...(doBanco.avisos || []),
              `a API ao vivo falhou (${explicarFalha(err)}); mostrando o último retrato gravado`,
            ],
          }
        }
      } catch {
        /* cai no arquivo abaixo */
      }
    }
    const arquivo = await doArquivo()
    return { ...arquivo, fonte: 'arquivo', motivoFallback: explicarFalha(err) }
  }
}

/**
 * Traduz a falha para algo que a analista entenda.
 *
 * O caso mais provável hoje não é o endpoint estar fora: é o nginx do VPS
 * devolver a página de login (302 → HTML). Sem este tratamento, a tela mostraria
 * "Unexpected token '<'", que não ajuda ninguém a saber o que fazer.
 */
function explicarFalha(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err)
  if (/Unexpected token|JSON|SyntaxError/i.test(msg)) {
    return 'o endpoint respondeu algo que não é JSON — provavelmente a tela de login do servidor'
  }
  if (/\b(401|403)\b/.test(msg)) return 'o endpoint recusou o acesso (sem autorização)'
  if (/\b404\b/.test(msg)) return 'o endpoint /reclamacoes ainda não existe neste servidor'
  if (/Failed to fetch|NetworkError/i.test(msg)) return 'não foi possível falar com o servidor'
  return `endpoint indisponível (${msg})`
}
